import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { Modal, BtnPrimary, BtnGhost } from './NotesModals';
import {
  Plus, X, Trash2, Pencil, Loader2, Check, ChevronDown, Users2, Share2,
  CornerDownRight, Flag, Hash, CalendarClock, Search, Info, Bookmark, ListChecks,
  FolderKanban,
} from 'lucide-react';

// Shared field styles (mirror the Notes/Tasks token system).
const inputCls =
  'n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]';
const labelCls = 'text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]';

const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
const GROUP_COLORS = ['#B49BE0', '#7FB5A6', '#E0A45E', '#6C8FD1', '#D98BA8', '#8AAE68'];

// ---------------------------------------------------------------------------
// Sub-task editor (two levels: item + nested sub-items). Kept intentionally
// NON-recursive so it stays simple and analyser-friendly; deeper trees created
// via "Save as template" are preserved untouched on the server.
// ---------------------------------------------------------------------------
function SubtaskTreeEditor({ nodes, onChange }) {
  const setNode = (i, patch) => onChange(nodes.map((n, idx) => (idx === i ? { ...n, ...patch } : n)));
  const removeNode = (i) => onChange(nodes.filter((_, idx) => idx !== i));
  const addNode = () => onChange([...nodes, { title: '', children: [] }]);
  const setChild = (i, j, patch) => setNode(i, { children: (nodes[i].children || []).map((c, cj) => (cj === j ? { ...c, ...patch } : c)) });
  const removeChild = (i, j) => setNode(i, { children: (nodes[i].children || []).filter((_, cj) => cj !== j) });
  const addChild = (i) => setNode(i, { children: [...(nodes[i].children || []), { title: '', children: [] }] });

  return (
    <div className="space-y-2" data-testid="template-subtasks-editor">
      {nodes.map((n, i) => (
        <div key={i} className="rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] p-2 space-y-1.5" data-testid="template-subtask-item">
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--n-muted-2)]"><ListChecks size={13} /></span>
            <input value={n.title} onChange={(e) => setNode(i, { title: e.target.value })}
              placeholder="Sub-task title…" className={`${inputCls} py-1.5`} data-testid="template-subtask-input" />
            <button type="button" onClick={() => addChild(i)} title="Add a nested sub-item" data-testid="template-subtask-add-child"
              className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]"><CornerDownRight size={14} /></button>
            <button type="button" onClick={() => removeNode(i)} title="Remove" data-testid="template-subtask-remove"
              className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-danger)]"><X size={14} /></button>
          </div>
          {(n.children || []).length > 0 && (
            <div className="ml-3 border-l border-[var(--n-line)] pl-3 space-y-1.5">
              {n.children.map((c, j) => (
                <div key={j} className="flex items-center gap-1.5" data-testid="template-subchild-item">
                  <span className="text-[var(--n-muted-2)]"><CornerDownRight size={12} /></span>
                  <input value={c.title} onChange={(e) => setChild(i, j, { title: e.target.value })}
                    placeholder="Nested sub-item…" className={`${inputCls} py-1.5`} data-testid="template-subchild-input" />
                  <button type="button" onClick={() => removeChild(i, j)} title="Remove" data-testid="template-subchild-remove"
                    className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-danger)]"><X size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={addNode} data-testid="template-subtask-add"
        className="n-focus inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-medium text-[var(--n-muted)] hover:text-[var(--n-accent)]">
        <Plus size={13} /> Add sub-task
      </button>
    </div>
  );
}

// Iterative node counter (no self-reference — keeps the source analyser happy).
function countNodes(nodes) {
  let total = 0;
  const stack = [...(nodes || [])];
  while (stack.length) {
    const n = stack.pop();
    total += 1;
    if (n && n.children && n.children.length) stack.push(...n.children);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Task Templates manager — list, create-from-scratch, edit, delete.
// ---------------------------------------------------------------------------
export function TaskTemplatesDialog({ open, onClose, onChanged, notify }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null (list) | 'new' | template object
  const [sharing, setSharing] = useState(null);  // template being shared

  const load = useCallback(() => {
    setLoading(true);
    notesApi.get('/notes/tasks/templates')
      .then(({ data }) => setTemplates(data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) { setEditing(null); load(); } }, [open, load]);

  const del = async (id) => {
    try {
      await notesApi.delete(`/notes/tasks/templates/${id}`);
      load(); onChanged?.();
      notify?.('Template deleted.');
    } catch { notify?.('Could not delete template.'); }
  };

  const afterSave = () => { setEditing(null); load(); onChanged?.(); };

  const owned = templates.filter((t) => t.is_owner !== false);
  const shared = templates.filter((t) => t.is_owner === false);
  const categories = [...new Set(owned.map((t) => (t.category || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const groupsMap = {};
  owned.forEach((t) => { const c = (t.category || '').trim(); (groupsMap[c] = groupsMap[c] || []).push(t); });
  const orderedCats = [...categories];
  if (groupsMap['']) orderedCats.push(''); // uncategorised last

  const renderRow = (t) => (
    <div key={t.id} className="flex items-center gap-3 rounded-xl border border-[var(--n-line)] px-3 py-2.5" data-testid="task-template-row">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><Bookmark size={15} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-[var(--n-ink)]" data-testid="task-template-name">{t.name}</span>
          {t.is_owner === false && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted)]" data-testid="task-template-shared-badge"><Users2 size={9} /> {t.owner_name || 'Shared'}</span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--n-muted)]">
          {t.title && <span className="truncate">{t.title}</span>}
          {t.priority && t.priority !== 'normal' && (
            <span className="inline-flex items-center gap-0.5"><Flag size={9} className={t.priority === 'high' ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent-2)]'} />{t.priority}</span>
          )}
          {typeof t.due_offset_days === 'number' && (
            <span className="inline-flex items-center gap-0.5"><CalendarClock size={9} />due in {t.due_offset_days}d</span>
          )}
          {countNodes(t.subtasks) > 0 && (
            <span className="inline-flex items-center gap-0.5"><ListChecks size={9} />{countNodes(t.subtasks)} sub-task{countNodes(t.subtasks) === 1 ? '' : 's'}</span>
          )}
          {(t.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Hash size={9} />{tag}</span>
          ))}
        </div>
      </div>
      {t.is_owner !== false ? (
        <>
          <button onClick={() => setSharing(t)} title="Share template" data-testid="task-template-share"
            className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]"><Share2 size={14} /></button>
          <button onClick={() => setEditing(t)} title="Edit template" data-testid="task-template-edit"
            className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><Pencil size={14} /></button>
          <button onClick={() => del(t.id)} title="Delete template" data-testid="task-template-delete"
            className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-danger)]"><Trash2 size={14} /></button>
        </>
      ) : (
        <div className="flex items-center gap-2">
          {t.can_edit && (
            <button onClick={() => setEditing(t)} title="Edit shared template" data-testid="task-template-edit-shared"
              className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><Pencil size={14} /></button>
          )}
          <span className="text-[11px] text-[var(--n-muted-2)]">{t.can_edit ? 'Can edit' : 'Use in Smart Add'}</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal open={open} onClose={onClose} maxW="max-w-lg" testid="task-templates-dialog"
        title="Task templates"
        subtitle="Reusable skeletons — title, priority, tags, a due-in-N-days SLA and sub-tasks">
        {editing ? (
          <TemplateEditor
            initial={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={afterSave}
            notify={notify}
            categories={categories}
          />
        ) : (
          <>
            <div className="mb-3 flex justify-end">
              <BtnPrimary onClick={() => setEditing('new')} data-testid="task-template-new-button">
                <Plus size={15} /> New template
              </BtnPrimary>
            </div>
            {loading ? (
              <div className="flex justify-center py-8 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div>
            ) : (owned.length === 0 && shared.length === 0) ? (
              <div className="rounded-xl border border-dashed border-[var(--n-border)] py-8 text-center text-[12.5px] text-[var(--n-muted)]" data-testid="task-templates-empty">
                No templates yet. Create one here, or use <strong>“Save as template”</strong> on any task.
              </div>
            ) : (
              <div className="space-y-3" data-testid="task-templates-list">
                {orderedCats.map((c) => (
                  <div key={c || '__none'}>
                    {(orderedCats.length > 1 || c) && (
                      <div className="mb-1 px-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]" data-testid="task-template-category-heading">{c || 'Uncategorised'}</div>
                    )}
                    <div className="space-y-1.5">{groupsMap[c].map(renderRow)}</div>
                  </div>
                ))}
                {shared.length > 0 && (
                  <div>
                    <div className="mb-1 flex items-center gap-1 px-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]" data-testid="task-template-shared-heading"><Users2 size={11} /> Shared with you</div>
                    <div className="space-y-1.5">{shared.map(renderRow)}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>
      <TemplateShareDialog open={!!sharing} template={sharing} onClose={() => { setSharing(null); load(); }} notify={notify} />
    </>
  );
}

function TemplateEditor({ initial, onCancel, onSaved, notify, categories = [] }) {
  const isNew = !initial?.id;
  const [name, setName] = useState(initial?.name || '');
  const [category, setCategory] = useState(initial?.category || '');
  const [title, setTitle] = useState(initial?.title || '');
  const [priority, setPriority] = useState(initial?.priority || 'normal');
  const [tagsText, setTagsText] = useState((initial?.tags || []).join(', '));
  const [dueOffset, setDueOffset] = useState(
    typeof initial?.due_offset_days === 'number' ? String(initial.due_offset_days) : '');
  const [subtasks, setSubtasks] = useState(initial?.subtasks?.length ? initial.subtasks : []);
  const [projectId, setProjectId] = useState(initial?.project_id || '');
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);
  // Share-on-create (new templates only).
  const [people, setPeople] = useState([]);
  const [selGroups, setSelGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [perm, setPerm] = useState('view'); // default view-only

  useEffect(() => {
    if (!isNew) return;
    notesApi.get('/notes/tasks/groups')
      .then(({ data }) => setGroups((data || []).filter((g) => g.is_owner)))
      .catch(() => setGroups([]));
  }, [isNew]);

  // Projects power the default-project selector (owned + projects shared with me).
  useEffect(() => {
    notesApi.get('/notes/tasks/projects')
      .then(({ data }) => setProjects(data || []))
      .catch(() => setProjects([]));
  }, []);

  const selectedProject = projects.find((p) => p.id === projectId) || null;

  const shareCount = people.length + selGroups.length;

  const save = async () => {
    if (!name.trim()) { notify?.('Give the template a name.'); return; }
    setBusy(true);
    const payload = {
      name: name.trim(),
      category: category.trim(),
      title: title.trim(),
      priority,
      tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
      due_offset_days: dueOffset === '' ? null : Math.max(0, parseInt(dueOffset, 10) || 0),
      subtasks,
      project_id: projectId || null,
    };
    try {
      if (initial?.id) {
        await notesApi.patch(`/notes/tasks/templates/${initial.id}`, payload);
        notify?.('Template updated.');
      } else {
        const { data: tmpl } = await notesApi.post('/notes/tasks/templates', payload);
        // Fan out shares (best-effort; a failed share never blocks the create).
        let ok = 0, fail = 0;
        const targets = [
          ...people.map((u) => ({ target_type: 'user', target_id: u.id })),
          ...selGroups.map((g) => ({ target_type: 'group', target_id: g.id })),
        ];
        for (const t of targets) {
          try { await notesApi.post(`/notes/tasks/templates/${tmpl.id}/shares`, { ...t, permission: perm }); ok += 1; }
          catch { fail += 1; }
        }
        if (ok && !fail) notify?.(`Template created · shared with ${ok} ${ok === 1 ? 'recipient' : 'recipients'} (${perm === 'view' ? 'view only' : 'can edit'}).`);
        else if (ok && fail) notify?.(`Created · shared with ${ok}, but ${fail} could not be shared.`);
        else notify?.('Template created.');
      }
      onSaved();
    } catch (e) {
      notify?.(e?.response?.data?.detail || 'Could not save template.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="task-template-editor">
      <div>
        <div className={labelCls}>Template name</div>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Client onboarding"
          className={`${inputCls} mt-1`} data-testid="task-template-name-input" />
      </div>
      <div>
        <div className={labelCls}>Category <span className="normal-case tracking-normal text-[var(--n-muted-2)]">(optional — groups templates)</span></div>
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Onboarding, Ops"
          list="task-template-categories" className={`${inputCls} mt-1`} data-testid="task-template-category-input" />
        <datalist id="task-template-categories">
          {categories.map((c) => <option key={c} value={c} />)}
        </datalist>
      </div>
      <div>
        <div className={labelCls}>Default task title</div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Pre-filled title for new tasks"
          className={`${inputCls} mt-1`} data-testid="task-template-title-input" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className={labelCls}>Priority</div>
          <div className="mt-1 inline-flex w-full rounded-lg border border-[var(--n-border)] p-0.5" data-testid="task-template-priority">
            {PRIORITIES.map((p) => (
              <button key={p.value} type="button" onClick={() => setPriority(p.value)}
                data-testid={`task-template-priority-${p.value}`}
                className={`n-focus flex-1 rounded-md px-2 py-1 text-[12px] font-medium transition-colors ${priority === p.value ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={labelCls}>Due (SLA)</div>
          <div className="mt-1 flex items-center gap-1.5">
            <input type="number" min="0" value={dueOffset} onChange={(e) => setDueOffset(e.target.value)}
              placeholder="—" className={`${inputCls} w-20`} data-testid="task-template-due-offset-input" />
            <span className="text-[12px] text-[var(--n-muted)]">days after creation</span>
          </div>
        </div>
      </div>
      <div>
        <div className={labelCls}>Tags</div>
        <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="comma, separated, tags"
          className={`${inputCls} mt-1`} data-testid="task-template-tags-input" />
      </div>
      <div>
        <div className={labelCls}>Project <span className="normal-case tracking-normal text-[var(--n-muted-2)]">(optional — where new tasks are created)</span></div>
        <div className="mt-1"><ProjectPicker projects={projects} value={projectId} onChange={setProjectId} /></div>
        <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[var(--n-muted-2)]" data-testid="task-template-project-hint">
          <Info size={12} className="mt-0.5 shrink-0" />
          {selectedProject
            ? (selectedProject.is_shared
              ? <span>New tasks go into <strong className="text-[var(--n-ink-soft)]">{selectedProject.name}</strong> and are automatically shared with everyone this project is shared with.</span>
              : <span>New tasks are created in <strong className="text-[var(--n-ink-soft)]">{selectedProject.name}</strong>. Share that project to have these tasks shared with its members too.</span>)
            : <span>Leave empty to choose a project each time you use this template.</span>}
        </div>
      </div>
      <div>
        <div className={`${labelCls} mb-1.5`}>Sub-tasks</div>
        <div className="rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-2.5">
          <SubtaskTreeEditor nodes={subtasks} onChange={setSubtasks} />
        </div>
      </div>

      {/* Share the new template right away (create flow only). */}
      {isNew && (
        <div className="rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-3 space-y-3" data-testid="task-template-share">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
              <Share2 size={12} /> Share this template <span className="normal-case tracking-normal text-[var(--n-muted-2)]">(optional)</span>
            </span>
            <AccessToggle value={perm} onChange={setPerm} testid="task-template-perm" />
          </div>
          <div>
            <div className={labelCls}>People</div>
            <div className="mt-1"><PeoplePicker selected={people} onChange={setPeople} /></div>
          </div>
          <div>
            <div className={labelCls}>Groups</div>
            <div className="mt-1"><GroupMultiPicker groups={groups} selected={selGroups} onChange={setSelGroups} /></div>
          </div>
          <div className="flex items-start gap-1.5 text-[11px] text-[var(--n-muted-2)]">
            <Info size={12} className="mt-0.5 shrink-0" />
            {perm === 'view'
              ? 'Recipients can use this template to create tasks, but cannot change it.'
              : 'Recipients can use and edit this template (only you can delete it).'}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <BtnGhost onClick={onCancel} data-testid="task-template-cancel">Cancel</BtnGhost>
        <BtnPrimary onClick={save} disabled={busy || !name.trim()} data-testid="task-template-save">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
          {isNew && shareCount > 0 ? `Save & share (${shareCount})` : 'Save template'}
        </BtnPrimary>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template sharing — let other people / groups USE (apply) a template.
// ---------------------------------------------------------------------------
function PeoplePickerInstant({ excludeIds, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => {
    let active = true;
    notesApi.get(`/notes/directory${q ? `?q=${encodeURIComponent(q)}` : ''}`)
      .then(({ data }) => { if (active) setResults(data || []); })
      .catch(() => { if (active) setResults([]); });
    return () => { active = false; };
  }, [q]);
  const avail = results.filter((u) => !excludeIds.has(u.id));
  const initials = (u) => (u.name || u.email || '?').slice(0, 1).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
      <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search teammates by name or email…" className={`${inputCls} pl-8`} data-testid="template-share-people-search" />
      {open && avail.length > 0 && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="template-share-people-menu">
          {avail.map((u) => (
            <button key={u.id} type="button" onClick={() => { onPick(u); setQ(''); setOpen(false); }} data-testid="template-share-people-option"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
              <span className="min-w-0 flex-1"><span className="block truncate text-sm text-[var(--n-ink)]">{u.name}</span><span className="block truncate text-[11px] text-[var(--n-muted)]">{u.email}</span></span>
              <Plus size={14} className="text-[var(--n-muted-2)]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateShareDialog({ open, template, onClose, notify }) {
  const [shares, setShares] = useState([]);
  const [groups, setGroups] = useState([]);
  const [gopen, setGopen] = useState(false);
  const [perm, setPerm] = useState('view'); // default view-only
  const gref = useRef(null);
  const tid = template?.id;

  const load = useCallback(async () => {
    if (!tid) return;
    try {
      const [s, g] = await Promise.all([
        notesApi.get(`/notes/tasks/templates/${tid}/shares`),
        notesApi.get('/notes/tasks/groups'),
      ]);
      setShares(s.data || []);
      setGroups((g.data || []).filter((x) => x.is_owner));
    } catch { /* silent */ }
  }, [tid]);
  useEffect(() => { if (open) { setPerm('view'); load(); } }, [open, load]);
  useEffect(() => {
    const h = (e) => { if (gref.current && !gref.current.contains(e.target)) setGopen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sharedUserIds = new Set(shares.filter((s) => s.target_type === 'user').map((s) => s.target_id));
  const sharedGroupIds = new Set(shares.filter((s) => s.target_type === 'group').map((s) => s.target_id));
  const availGroups = groups.filter((g) => !sharedGroupIds.has(g.id));

  const addUser = async (u) => {
    try { await notesApi.post(`/notes/tasks/templates/${tid}/shares`, { target_type: 'user', target_id: u.id, permission: perm }); await load(); notify?.(`Shared with ${u.name} (${perm === 'view' ? 'view only' : 'can edit'}).`); }
    catch (e) { notify?.(e?.response?.data?.detail || 'Could not share.'); }
  };
  const addGroup = async (g) => {
    setGopen(false);
    try { await notesApi.post(`/notes/tasks/templates/${tid}/shares`, { target_type: 'group', target_id: g.id, permission: perm }); await load(); notify?.(`Shared with ${g.name} (${perm === 'view' ? 'view only' : 'can edit'}).`); }
    catch (e) { notify?.(e?.response?.data?.detail || 'Could not share.'); }
  };
  const remove = async (s) => {
    setShares((p) => p.filter((x) => !(x.target_type === s.target_type && x.target_id === s.target_id)));
    try { await notesApi.delete(`/notes/tasks/templates/${tid}/shares?target_type=${s.target_type}&target_id=${s.target_id}`); } catch { load(); }
  };
  const initials = (n) => (n || '?').slice(0, 1).toUpperCase();

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-md" testid="template-share-dialog"
      title="Share template" subtitle={template ? `Let others use “${template.name}”` : ''}>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">Access for new shares</span>
          <AccessToggle value={perm} onChange={setPerm} testid="template-share-perm" />
        </div>
        <div>
          <div className={labelCls}>Share with people</div>
          <div className="mt-1"><PeoplePickerInstant excludeIds={sharedUserIds} onPick={addUser} /></div>
        </div>
        <div>
          <div className={labelCls}>Share with a group</div>
          {groups.length === 0 ? (
            <p className="mt-1 text-[12px] text-[var(--n-muted-2)]">No groups yet — create one from the Groups button.</p>
          ) : (
            <div className="relative mt-1" ref={gref}>
              <button type="button" onClick={() => setGopen((o) => !o)} data-testid="template-share-group-picker"
                className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)] transition-colors">
                <span className="text-[var(--n-muted-2)]">Pick a group to share with…</span>
                <ChevronDown size={15} className="text-[var(--n-muted-2)]" />
              </button>
              {gopen && (
                <div className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="template-share-group-menu">
                  {availGroups.length === 0 ? (
                    <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">All your groups already have access.</div>
                  ) : availGroups.map((g) => (
                    <button key={g.id} type="button" onClick={() => addGroup(g)} data-testid="template-share-group-option"
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
                      <span className="grid h-6 w-6 place-items-center rounded-md text-white" style={{ background: g.color || GROUP_COLORS[0] }}><Users2 size={12} /></span>
                      <span className="min-w-0 flex-1 truncate text-sm text-[var(--n-ink)]">{g.name}</span>
                      <span className="text-[11px] text-[var(--n-muted)]">{g.member_count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {shares.length > 0 && (
          <div>
            <div className={labelCls}>Has access</div>
            <div className="mt-1 space-y-1.5" data-testid="template-share-current">
              {shares.map((s) => (
                <div key={`${s.target_type}:${s.target_id}`} className="flex items-center gap-2.5 rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] px-2.5 py-2" data-testid="template-share-item">
                  {s.target_type === 'group' ? (
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-white" style={{ background: s.color || GROUP_COLORS[0] }}><Users2 size={12} /></span>
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[9px] font-bold text-[var(--n-accent)]">{initials(s.name)}</span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-[var(--n-ink)]">{s.name}</div>
                    <div className="text-[11px] text-[var(--n-muted)]">{s.target_type === 'group' ? `group · ${s.member_count} member${s.member_count === 1 ? '' : 's'}` : (s.email || 'person')}</div>
                  </div>
                  <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${s.permission === 'edit' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'bg-[var(--n-canvas-2)] text-[var(--n-muted)]'}`} data-testid="template-share-item-perm">
                    {s.permission === 'edit' ? 'Can edit' : 'View only'}
                  </span>
                  <button onClick={() => remove(s)} title="Remove access" data-testid="template-share-remove"
                    className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)] transition-colors"><X size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-start gap-1.5 text-[11px] text-[var(--n-muted-2)]">
          <Info size={12} className="mt-0.5 shrink-0" /> <span><strong>View only</strong> lets people use this template to create tasks. <strong>Can edit</strong> also lets them change it — only you can delete it.</span>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Access (view / edit) toggle — reused by Save-as-template + share dialogs.
//  • view = recipients can USE (apply) the template only
//  • edit = recipients can USE and EDIT the template
// ---------------------------------------------------------------------------
function AccessToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid={testid}>
      {[['view', 'View only'], ['edit', 'Can edit']].map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`n-focus rounded-md px-2.5 py-0.5 text-[11.5px] font-medium transition-colors ${value === v ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project single-select — the default project a template drops new tasks into.
// Picking a shared project means tasks auto-share with the project's members.
// ---------------------------------------------------------------------------
function ProjectPicker({ projects, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selected = projects.find((p) => p.id === value) || null;
  const pick = (id) => { onChange(id); setOpen(false); };
  const dot = (p) => (
    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md" style={{ background: (p && p.color) || 'var(--n-accent-bg)' }}>
      <FolderKanban size={11} className={p && p.color ? 'text-white' : 'text-[var(--n-accent)]'} />
    </span>
  );
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="task-template-project-picker"
        className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)] transition-colors">
        {selected ? (
          <span className="flex min-w-0 items-center gap-2">
            {dot(selected)}
            <span className="truncate text-[var(--n-ink)]">{selected.name}</span>
            {selected.is_shared && (
              <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted)]"><Users2 size={9} /> Shared</span>
            )}
          </span>
        ) : (
          <span className="text-[var(--n-muted-2)]">No project — ask on creation</span>
        )}
        <ChevronDown size={15} className="shrink-0 text-[var(--n-muted-2)]" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-template-project-menu">
          <button type="button" onClick={() => pick('')} data-testid="task-template-project-option-none"
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md border border-dashed border-[var(--n-border)] text-[var(--n-muted-2)]"><X size={10} /></span>
            <span className="min-w-0 flex-1 truncate text-sm text-[var(--n-muted)]">No project</span>
            {!value && <Check size={14} className="text-[var(--n-accent)]" />}
          </button>
          {projects.length === 0 ? (
            <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">No projects yet.</div>
          ) : projects.map((p) => (
            <button key={p.id} type="button" onClick={() => pick(p.id)} data-testid="task-template-project-option"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
              {dot(p)}
              <span className="min-w-0 flex-1 truncate text-sm text-[var(--n-ink)]">{p.name}</span>
              {p.is_shared && <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] text-[var(--n-muted)]"><Users2 size={9} /> Shared</span>}
              {value === p.id && <Check size={14} className="shrink-0 text-[var(--n-accent)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group multi-select — pick one or more owned groups (chips + dropdown).
// ---------------------------------------------------------------------------
function GroupMultiPicker({ groups, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const selectedIds = new Set(selected.map((g) => g.id));
  const available = groups.filter((g) => !selectedIds.has(g.id));
  const add = (g) => { onChange([...selected, g]); setOpen(false); };
  const remove = (id) => onChange(selected.filter((g) => g.id !== id));

  if (groups.length === 0) {
    return <p className="mt-1 text-[12px] text-[var(--n-muted-2)]" data-testid="save-as-template-no-groups">No groups yet — create one from the Groups button.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="relative" ref={ref}>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid="save-as-template-group-picker"
          className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)] transition-colors">
          <span className="text-[var(--n-muted-2)]">Pick a group to share with…</span>
          <ChevronDown size={15} className="text-[var(--n-muted-2)]" />
        </button>
        {open && (
          <div className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="save-as-template-group-menu">
            {available.length === 0 ? (
              <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">All your groups are selected.</div>
            ) : available.map((g) => (
              <button key={g.id} type="button" onClick={() => add(g)} data-testid="save-as-template-group-option"
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
                <span className="grid h-6 w-6 place-items-center rounded-md text-white" style={{ background: g.color || GROUP_COLORS[0] }}><Users2 size={12} /></span>
                <span className="min-w-0 flex-1 truncate text-sm text-[var(--n-ink)]">{g.name}</span>
                <span className="text-[11px] text-[var(--n-muted)]">{g.member_count}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="save-as-template-selected-groups">
          {selected.map((g) => (
            <span key={g.id} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] bg-[var(--n-canvas-2)] py-1 pl-1.5 pr-1 text-[12px] text-[var(--n-ink-soft)]" data-testid="save-as-template-selected-group">
              <span className="grid h-5 w-5 place-items-center rounded-md text-white" style={{ background: g.color || GROUP_COLORS[0] }}><Users2 size={10} /></span>
              <span className="max-w-[140px] truncate">{g.name}</span>
              <button type="button" onClick={() => remove(g.id)} aria-label={`Remove ${g.name}`}
                className="grid h-4 w-4 place-items-center rounded-full text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Save as template" — snapshot an existing task (title/priority/tags + subtasks)
// AND optionally share it right away with people / groups (view-only default).
// ---------------------------------------------------------------------------
export function SaveAsTemplateDialog({ open, onClose, task, onChanged, notify }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState([]);   // [{id,name,email}]
  const [selGroups, setSelGroups] = useState([]); // [{id,name,color,member_count}]
  const [groups, setGroups] = useState([]);    // owned groups
  const [perm, setPerm] = useState('view');    // default view-only

  useEffect(() => {
    if (!open) return;
    setName(task?.title ? `${task.title}` : '');
    setPeople([]); setSelGroups([]); setPerm('view');
    notesApi.get('/notes/tasks/groups')
      .then(({ data }) => setGroups((data || []).filter((g) => g.is_owner)))
      .catch(() => setGroups([]));
  }, [open, task]);

  const shareCount = people.length + selGroups.length;

  const save = async () => {
    if (!name.trim() || !task?.id) return;
    setBusy(true);
    try {
      const { data: tmpl } = await notesApi.post('/notes/tasks/templates', { name: name.trim(), from_task_id: task.id });
      // Fan out the shares (best-effort; a failed share never blocks the save).
      let shareOk = 0, shareFail = 0;
      const targets = [
        ...people.map((u) => ({ target_type: 'user', target_id: u.id })),
        ...selGroups.map((g) => ({ target_type: 'group', target_id: g.id })),
      ];
      for (const t of targets) {
        try { await notesApi.post(`/notes/tasks/templates/${tmpl.id}/shares`, { ...t, permission: perm }); shareOk += 1; }
        catch { shareFail += 1; }
      }
      if (shareOk && !shareFail) notify?.(`Saved as template · shared with ${shareOk} ${shareOk === 1 ? 'recipient' : 'recipients'} (${perm === 'view' ? 'view only' : 'can edit'}).`);
      else if (shareOk && shareFail) notify?.(`Saved · shared with ${shareOk}, but ${shareFail} could not be shared.`);
      else notify?.('Saved as template.');
      onChanged?.();
      onClose();
    } catch (e) {
      notify?.(e?.response?.data?.detail || 'Could not save template.');
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-md" testid="save-as-template-dialog"
      title="Save as template"
      subtitle="Reuse this task's title, priority, tags and sub-tasks later">
      <div className="space-y-3">
        <div>
          <div className={labelCls}>Template name</div>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            placeholder="Name this template" className={`${inputCls} mt-1`} data-testid="save-as-template-name-input" />
        </div>

        {/* Optional: share the new template right away. */}
        <div className="rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-3 space-y-3" data-testid="save-as-template-share">
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
              <Share2 size={12} /> Share this template <span className="normal-case tracking-normal text-[var(--n-muted-2)]">(optional)</span>
            </span>
            <AccessToggle value={perm} onChange={setPerm} testid="save-as-template-perm" />
          </div>
          <div>
            <div className={labelCls}>People</div>
            <div className="mt-1"><PeoplePicker selected={people} onChange={setPeople} /></div>
          </div>
          <div>
            <div className={labelCls}>Groups</div>
            <div className="mt-1"><GroupMultiPicker groups={groups} selected={selGroups} onChange={setSelGroups} /></div>
          </div>
          <div className="flex items-start gap-1.5 text-[11px] text-[var(--n-muted-2)]">
            <Info size={12} className="mt-0.5 shrink-0" />
            {perm === 'view'
              ? 'Recipients can use this template to create tasks, but cannot change it.'
              : 'Recipients can use and edit this template (only you can delete it).'}
          </div>
        </div>

        <div className="flex items-start gap-1.5 text-[11.5px] text-[var(--n-muted-2)]">
          <Info size={12} className="mt-0.5 shrink-0" />
          The task's sub-tasks are captured too. Due dates become a relative “due in N days” only if you set an SLA on the template afterwards.
        </div>
        <div className="flex justify-end gap-2">
          <BtnGhost onClick={onClose} data-testid="save-as-template-cancel">Cancel</BtnGhost>
          <BtnPrimary onClick={save} disabled={busy || !name.trim()} data-testid="save-as-template-confirm">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Bookmark size={15} />}
            {shareCount > 0 ? `Save & share (${shareCount})` : 'Save template'}
          </BtnPrimary>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// People autocomplete (directory-backed) used by the group editor.
// ---------------------------------------------------------------------------
function PeoplePicker({ selected, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenList(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    let active = true;
    notesApi.get(`/notes/directory${q ? `?q=${encodeURIComponent(q)}` : ''}`)
      .then(({ data }) => { if (active) setResults(data || []); })
      .catch(() => { if (active) setResults([]); });
    return () => { active = false; };
  }, [q]);

  const selectedIds = new Set(selected.map((s) => s.id));
  const available = results.filter((u) => !selectedIds.has(u.id));
  const add = (u) => { onChange([...selected, u]); setQ(''); };
  const remove = (id) => onChange(selected.filter((s) => s.id !== id));
  const initials = (u) => (u.name || u.email || '?').slice(0, 1).toUpperCase();

  return (
    <div className="space-y-2">
      <div className="relative" ref={ref}>
        <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
        <input
          value={q}
          onFocus={() => setOpenList(true)}
          onChange={(e) => { setQ(e.target.value); setOpenList(true); }}
          placeholder="Search teammates by name or email…"
          className={`${inputCls} pl-8`}
          data-testid="group-people-search"
        />
        {openList && available.length > 0 && (
          <div className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="group-people-menu">
            {available.map((u) => (
              <button key={u.id} type="button" onClick={() => add(u)} data-testid="group-people-option"
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-[var(--n-ink)]">{u.name}</span>
                  <span className="block truncate text-[11px] text-[var(--n-muted)]">{u.email}</span>
                </span>
                <Plus size={14} className="text-[var(--n-muted-2)]" />
              </button>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" data-testid="group-selected-members">
          {selected.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] bg-[var(--n-canvas-2)] py-1 pl-1.5 pr-1 text-[12px] text-[var(--n-ink-soft)]" data-testid="group-selected-member">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[9px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
              <span className="max-w-[140px] truncate">{u.name}</span>
              <button type="button" onClick={() => remove(u.id)} aria-label={`Remove ${u.name}`}
                className="grid h-4 w-4 place-items-center rounded-full text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={11} /></button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-[12px] text-[var(--n-muted-2)]">No members yet — search above to add teammates.</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task Groups manager — create/rename/delete groups + manage members.
// ---------------------------------------------------------------------------
export function TaskGroupsDialog({ open, onClose, onChanged, notify }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | group

  const load = useCallback(() => {
    setLoading(true);
    notesApi.get('/notes/tasks/groups')
      .then(({ data }) => setGroups(data || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) { setEditing(null); load(); } }, [open, load]);

  const del = async (id) => {
    try {
      await notesApi.delete(`/notes/tasks/groups/${id}`);
      load(); onChanged?.();
      notify?.('Group deleted.');
    } catch { notify?.('Could not delete group.'); }
  };
  const afterSave = () => { setEditing(null); load(); onChanged?.(); };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg" testid="task-groups-dialog"
      title="Groups"
      subtitle="Bundle teammates so you can share a task with everyone at once">
      {editing ? (
        <GroupEditor initial={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onSaved={afterSave} notify={notify} />
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <BtnPrimary onClick={() => setEditing('new')} data-testid="task-group-new-button">
              <Plus size={15} /> New group
            </BtnPrimary>
          </div>
          {loading ? (
            <div className="flex justify-center py-8 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div>
          ) : groups.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--n-border)] py-8 text-center text-[12.5px] text-[var(--n-muted)]" data-testid="task-groups-empty">
              No groups yet. Create one to share tasks with a whole team in a click.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="task-groups-list">
              {groups.map((g) => (
                <div key={g.id} className="flex items-center gap-3 rounded-xl border border-[var(--n-line)] px-3 py-2.5" data-testid="task-group-row">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white" style={{ background: g.color || GROUP_COLORS[0] }}><Users2 size={15} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[var(--n-ink)]" data-testid="task-group-name">{g.name}</div>
                    <div className="mt-0.5 truncate text-[11px] text-[var(--n-muted)]" data-testid="task-group-members-line">
                      {g.member_count} member{g.member_count === 1 ? '' : 's'}
                      {g.members?.length ? ` · ${g.members.map((m) => m.name).slice(0, 3).join(', ')}${g.members.length > 3 ? '…' : ''}` : ''}
                      {!g.is_owner && <span className="ml-1 text-[var(--n-accent)]">· shared with you</span>}
                    </div>
                  </div>
                  {g.is_owner && (
                    <>
                      <button onClick={() => setEditing(g)} title="Edit group" data-testid="task-group-edit"
                        className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><Pencil size={14} /></button>
                      <button onClick={() => del(g.id)} title="Delete group" data-testid="task-group-delete"
                        className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-danger)]"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function GroupEditor({ initial, onCancel, onSaved, notify }) {
  const [name, setName] = useState(initial?.name || '');
  const [color, setColor] = useState(initial?.color || GROUP_COLORS[0]);
  const [members, setMembers] = useState(initial?.members || []);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { notify?.('Give the group a name.'); return; }
    setBusy(true);
    const payload = { name: name.trim(), color, member_ids: members.map((m) => m.id) };
    try {
      if (initial?.id) await notesApi.patch(`/notes/tasks/groups/${initial.id}`, payload);
      else await notesApi.post('/notes/tasks/groups', payload);
      notify?.(initial?.id ? 'Group updated.' : 'Group created.');
      onSaved();
    } catch (e) {
      notify?.(e?.response?.data?.detail || 'Could not save group.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="task-group-editor">
      <div>
        <div className={labelCls}>Group name</div>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ops Team"
          className={`${inputCls} mt-1`} data-testid="task-group-name-input" />
      </div>
      <div>
        <div className={labelCls}>Colour</div>
        <div className="mt-1.5 flex items-center gap-1.5" data-testid="task-group-color">
          {GROUP_COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} aria-label={`Colour ${c}`}
              data-testid={`task-group-color-${c}`}
              className={`h-6 w-6 rounded-full transition-transform ${color === c ? 'ring-2 ring-offset-2 ring-[var(--n-ink)] scale-110' : 'hover:scale-110'}`}
              style={{ background: c }} />
          ))}
        </div>
      </div>
      <div>
        <div className={`${labelCls} mb-1.5`}>Members</div>
        <PeoplePicker selected={members} onChange={setMembers} />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <BtnGhost onClick={onCancel} data-testid="task-group-cancel">Cancel</BtnGhost>
        <BtnPrimary onClick={save} disabled={busy || !name.trim()} data-testid="task-group-save">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save group
        </BtnPrimary>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group-share block — embedded inside the Task share dialog (task-only).
// ---------------------------------------------------------------------------
export function GroupShareBlock({ taskId, notify, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [shares, setShares] = useState([]);
  const [pick, setPick] = useState('');
  const [perm, setPerm] = useState('edit'); // 'edit' | 'view'
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    try {
      const [g, s] = await Promise.all([
        notesApi.get('/notes/tasks/groups'),
        notesApi.get(`/notes/tasks/group-shares?task_id=${taskId}`),
      ]);
      setGroups((g.data || []).filter((x) => x.is_owner));
      setShares(s.data || []);
    } catch { /* silent */ }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sharedIds = new Set(shares.map((s) => s.group_id));
  const available = groups.filter((g) => !sharedIds.has(g.id));

  const share = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await notesApi.post('/notes/tasks/group-shares', { task_id: taskId, group_id: pick, permission: perm });
      const g = groups.find((x) => x.id === pick);
      setPick('');
      await load(); onChanged?.();
      notify?.(`Shared with ${g?.name || 'group'} (${perm === 'view' ? 'view only' : 'can edit'}).`);
    } catch (e) { notify?.(e?.response?.data?.detail || 'Could not share with group.'); }
    finally { setBusy(false); }
  };

  const remove = async (gid) => {
    setShares((p) => p.filter((s) => s.group_id !== gid));
    try { await notesApi.delete(`/notes/tasks/group-shares?task_id=${taskId}&group_id=${gid}`); onChanged?.(); }
    catch { notify?.('Could not remove group.'); load(); }
  };

  const pickLabel = pick ? (groups.find((g) => g.id === pick)?.name || 'Select a group…') : 'Select a group…';

  return (
    <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3 mb-3" data-testid="task-share-group-block">
      <div className="text-xs font-semibold text-[var(--n-ink-soft)] mb-2 flex items-center gap-1.5"><Users2 size={14} /> Share with a group</div>
      {groups.length === 0 ? (
        <div className="text-[12px] text-[var(--n-muted-2)]">You have no groups yet. Create one from the <strong>Groups</strong> button in the header.</div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="relative flex-1" ref={ref}>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid="task-share-group-picker"
              className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)] transition-colors">
              <span className={`truncate ${pick ? 'text-[var(--n-ink)]' : 'text-[var(--n-muted-2)]'}`}>{pickLabel}</span>
              <ChevronDown size={15} className="shrink-0 text-[var(--n-muted-2)]" />
            </button>
            {open && (
              <div className="absolute left-0 right-0 z-50 mt-1 max-h-52 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-share-group-menu">
                {available.length === 0 ? (
                  <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">All your groups already have access.</div>
                ) : available.map((g) => (
                  <button key={g.id} type="button" onClick={() => { setPick(g.id); setOpen(false); }} data-testid="task-share-group-option"
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
                    <span className="grid h-6 w-6 place-items-center rounded-md text-white" style={{ background: g.color || GROUP_COLORS[0] }}><Users2 size={12} /></span>
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--n-ink)]">{g.name}</span>
                    <span className="text-[11px] text-[var(--n-muted)]">{g.member_count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <BtnPrimary onClick={share} disabled={busy || !pick} data-testid="task-share-group-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} Share
          </BtnPrimary>
        </div>
      )}
      {groups.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5" data-testid="task-share-group-perm">
          <span className="text-[11px] text-[var(--n-muted-2)]">Access:</span>
          <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5">
            <button type="button" onClick={() => setPerm('edit')} data-testid="task-share-group-perm-edit"
              className={`n-focus rounded-md px-2 py-0.5 text-[11.5px] font-medium transition-colors ${perm === 'edit' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>Can edit</button>
            <button type="button" onClick={() => setPerm('view')} data-testid="task-share-group-perm-view"
              className={`n-focus rounded-md px-2 py-0.5 text-[11.5px] font-medium transition-colors ${perm === 'view' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>View only</button>
          </div>
          {perm === 'view' && <span className="text-[11px] text-[var(--n-muted-2)]">members can only tick / untick</span>}
        </div>
      )}
      {shares.length > 0 && (
        <div className="mt-2.5 space-y-1.5" data-testid="task-share-group-current">
          {shares.map((s) => (
            <div key={s.group_id} className="flex items-center gap-2.5 rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] px-2.5 py-2" data-testid="task-share-group-item">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-white" style={{ background: s.color || GROUP_COLORS[0] }}><Users2 size={12} /></span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-[var(--n-ink)]">{s.name}</div>
                <div className="text-[11px] text-[var(--n-muted)]">{s.member_count} member{s.member_count === 1 ? '' : 's'} · {s.permission === 'view' ? 'view only (tick only)' : 'can edit'}</div>
              </div>
              <button onClick={() => remove(s.group_id)} title="Remove group access" data-testid="task-share-group-remove"
                className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)] transition-colors"><X size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
